import { type FC, useCallback, useState } from "react";

import type {
  DashboardAdminKeystoreSpendingEntry,
  DashboardKeystoreEntry,
} from "../../dashboard-client";

import {
  BEDROCK_MODEL_OPTIONS,
  MULTI_FIELD_PROVIDER_KEYS,
  PROVIDER_LABELS,
  PROVIDER_OPTIONS,
} from "../../constants/providers";
import { useAdmin } from "../../hooks/useAdmin";
import { useFormAction } from "../../hooks/useFormAction";
import { useKeystore } from "../../hooks/useKeystore";
import { formatRelativeTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { FormInput } from "../common/forms/FormInput";
import { FormSelect } from "../common/forms/FormSelect";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { StatsGrid } from "../common/StatsGrid";
import { Text } from "../common/Text";

const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Available", value: "available" },
  { label: "Assigned", value: "assigned" },
];

const PROVIDER_FILTER_OPTIONS = [
  { label: "All Providers", value: "" },
  ...PROVIDER_OPTIONS,
];
const BEDROCK_ARN_PLACEHOLDER =
  "arn:aws:bedrock:eu-west-1:123456789012:inference-profile/...";

function getProviderKeyIdOptions(provider: string) {
  if (provider === "bedrock") {
    return BEDROCK_MODEL_OPTIONS;
  }
  return (
    MULTI_FIELD_PROVIDER_KEYS[provider]?.map((field) => ({
      label: field.label,
      value: field.value,
    })) ?? []
  );
}

function getDefaultProviderKeyId(provider: string): string {
  return getProviderKeyIdOptions(provider)[0]?.value ?? "";
}

function currentMonthRange(): { start: string; end: string } {
  const now = new Date();
  const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const end = now.toISOString().slice(0, 10);
  return { end, start };
}

export const AdminKeystoreTab: FC = () => {
  const {
    entries,
    loading,
    error,
    create,
    remove,
    assign,
    unassign,
    spendingMap,
    spendingLoading,
    spendingWarnings,
    refreshSpending,
  } = useKeystore();
  const { organizations } = useAdmin();

  const [statusFilter, setStatusFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");

  const defaultRange = currentMonthRange();
  const [spendingStart, setSpendingStart] = useState(defaultRange.start);
  const [spendingEnd, setSpendingEnd] = useState(defaultRange.end);

  const [newProvider, setNewProvider] = useState("anthropic");
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newProviderKeyId, setNewProviderKeyId] = useState("");
  const {
    saving: creating,
    error: createError,
    run: runCreate,
  } = useFormAction();

  const handleCreate = useCallback(async () => {
    if (!newKey.trim()) {
      return;
    }
    const providerKeyIdOptions = getProviderKeyIdOptions(newProvider);
    if (providerKeyIdOptions.length > 0 && !newProviderKeyId.trim()) {
      return;
    }
    const ok = await runCreate(async () => {
      await create(
        newProvider,
        newKey.trim(),
        newLabel.trim() || undefined,
        providerKeyIdOptions.length > 0
          ? newProviderKeyId.trim() || undefined
          : undefined,
      );
      return undefined;
    });
    if (ok) {
      setNewKey("");
      setNewLabel("");
      setNewProviderKeyId(getDefaultProviderKeyId(newProvider));
    }
  }, [newProvider, newKey, newLabel, newProviderKeyId, create, runCreate]);

  const handleProviderChange = useCallback((provider: string) => {
    setNewProvider(provider);
    setNewProviderKeyId(getDefaultProviderKeyId(provider));
  }, []);

  const handleUpdateSpending = useCallback(() => {
    void refreshSpending(spendingStart, spendingEnd);
  }, [refreshSpending, spendingStart, spendingEnd]);

  if (loading) {
    return <LoadingState message="Loading keystore..." />;
  }
  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  const filtered = entries.filter((e) => {
    if (statusFilter && e.status !== statusFilter) {
      return false;
    }
    if (providerFilter && e.provider !== providerFilter) {
      return false;
    }
    return true;
  });
  const bedrockEntries = entries.filter(
    (entry) => entry.provider === "bedrock",
  );
  const isCreatingBedrockEntry = newProvider === "bedrock";
  const providerKeyIdOptions = getProviderKeyIdOptions(newProvider);
  const selectedMultiField = MULTI_FIELD_PROVIDER_KEYS[newProvider]?.find(
    (field) => field.value === newProviderKeyId,
  );
  const keyLabel = isCreatingBedrockEntry
    ? "Inference Profile ARN"
    : (selectedMultiField?.label ?? "API Key");
  const keyPlaceholder = isCreatingBedrockEntry
    ? BEDROCK_ARN_PLACEHOLDER
    : (selectedMultiField?.placeholder ?? "sk-...");
  const keyInputType =
    isCreatingBedrockEntry || selectedMultiField?.isSecret === false
      ? "text"
      : "password";
  const canCreateEntry =
    newKey.trim().length > 0 &&
    (providerKeyIdOptions.length === 0 || newProviderKeyId.trim().length > 0);

  return (
    <div className="space-y-4">
      {/* Add Key Form */}
      <PanelSection title="Add Key to Pool">
        {createError && (
          <Alert variant="error" size="sm" className="mb-3">
            {createError}
          </Alert>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="w-full sm:w-40">
            <FormSelect
              label="Provider"
              options={PROVIDER_OPTIONS}
              value={newProvider}
              onChange={(e) => handleProviderChange(e.target.value)}
            />
          </div>
          <div className="w-full sm:min-w-[220px] sm:flex-1">
            <FormInput
              label={keyLabel}
              type={keyInputType}
              placeholder={keyPlaceholder}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
          </div>
          {providerKeyIdOptions.length > 0 && (
            <div className="w-full sm:w-64">
              <FormSelect
                label={isCreatingBedrockEntry ? "Model ID" : "Credential Field"}
                options={providerKeyIdOptions}
                value={newProviderKeyId}
                onValue={(value) => setNewProviderKeyId(value)}
              />
            </div>
          )}
          <div className="w-full sm:w-48">
            <FormInput
              label="Label (optional)"
              type="text"
              placeholder="e.g. Pool key #1"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleCreate()}
            loading={creating}
            loadingText="Adding..."
            disabled={!canCreateEntry}
          >
            Add to Pool
          </Button>
        </div>
        {isCreatingBedrockEntry && (
          <Text as="p" className="mt-3 max-w-4xl leading-5">
            Register the inference profile ARN used for the selected Bedrock
            model ID. Platform spend is attributed from this model-to-ARN
            mapping plus the entry&apos;s current organization assignment.
          </Text>
        )}
      </PanelSection>

      {/* Spending Date Range */}
      <div className="flex items-end gap-3">
        <div className="w-40">
          <FormInput
            label="From"
            type="date"
            size="sm"
            value={spendingStart}
            onChange={(e) => setSpendingStart(e.target.value)}
          />
        </div>
        <div className="w-40">
          <FormInput
            label="To"
            type="date"
            size="sm"
            value={spendingEnd}
            onChange={(e) => setSpendingEnd(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleUpdateSpending}
          loading={spendingLoading}
          loadingText="Loading..."
        >
          Update
        </Button>
      </div>

      {spendingWarnings.length > 0 && (
        <Alert variant="warning" size="sm">
          {spendingWarnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </Alert>
      )}

      {bedrockEntries.length > 0 && (
        <BedrockArnSpendPanel
          entries={bedrockEntries}
          spendingLoading={spendingLoading}
          spendingMap={spendingMap}
        />
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="w-36">
          <FormSelect
            label="Status"
            size="sm"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
        <div className="w-40">
          <FormSelect
            label="Provider"
            size="sm"
            options={PROVIDER_FILTER_OPTIONS}
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
          />
        </div>
        <Text className="mt-5">
          {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
        </Text>
      </div>

      {/* Table */}
      <DataTable responsive>
        <DataTable.Header>
          <tr>
            <DataTable.HeaderCell>Provider</DataTable.HeaderCell>
            <DataTable.HeaderCell>Label</DataTable.HeaderCell>
            <DataTable.HeaderCell>Status</DataTable.HeaderCell>
            <DataTable.HeaderCell>Assigned To</DataTable.HeaderCell>
            <DataTable.HeaderCell>Spending</DataTable.HeaderCell>
            <DataTable.HeaderCell>Created</DataTable.HeaderCell>
            <DataTable.HeaderCell>Actions</DataTable.HeaderCell>
          </tr>
        </DataTable.Header>
        <DataTable.Body>
          {filtered.length === 0 ? (
            <DataTable.Row>
              <DataTable.Cell
                colSpan={7}
                className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No keystore entries found.
              </DataTable.Cell>
            </DataTable.Row>
          ) : (
            filtered.map((entry) => (
              <KeystoreRow
                key={entry.entry_id}
                entry={entry}
                organizations={organizations}
                spendingInfo={spendingMap.get(entry.entry_id)}
                spendingLoading={spendingLoading}
                onAssign={assign}
                onUnassign={unassign}
                onDelete={remove}
              />
            ))
          )}
        </DataTable.Body>
      </DataTable>
    </div>
  );
};

function formatSpending(
  info: DashboardAdminKeystoreSpendingEntry | undefined,
  isLoading: boolean,
): { text: string; className: string } {
  if (isLoading) {
    return { className: "text-gray-400 dark:text-gray-500", text: "..." };
  }
  if (!info) {
    return { className: "text-gray-400 dark:text-gray-500", text: "—" };
  }
  if (info.error === "unsupported_provider") {
    return { className: "text-gray-400 dark:text-gray-500", text: "N/A" };
  }
  if (info.error === "backfill_not_supported") {
    return { className: "text-gray-400 dark:text-gray-500", text: "N/A" };
  }
  if (info.error === "unassigned_entry") {
    return {
      className: "text-gray-400 dark:text-gray-500",
      text: "Unassigned",
    };
  }
  if (info.error === "missing_provider_key") {
    return {
      className: "text-amber-600 dark:text-amber-400",
      text: "Missing model",
    };
  }
  if (info.error) {
    return { className: "text-red-600 dark:text-red-400", text: "Error" };
  }
  const amount = info.amount_usd ?? 0;
  return {
    className: "text-gray-900 dark:text-white font-medium",
    text: `$${amount.toFixed(2)}`,
  };
}

function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function truncateMiddle(value: string, keepStart = 28, keepEnd = 18): string {
  if (value.length <= keepStart + keepEnd + 3) {
    return value;
  }
  return `${value.slice(0, keepStart)}...${value.slice(-keepEnd)}`;
}

function readBedrockInferenceProfileArn(
  entry: DashboardKeystoreEntry & {
    bedrock_inference_profile_arn?: string | null;
  },
): string | null {
  return entry.bedrock_inference_profile_arn ?? null;
}

const BedrockArnCell: FC<{ arn: string | null }> = ({ arn }) => {
  if (!arn) {
    return (
      <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
    );
  }

  return (
    <div className="space-y-1">
      <div
        className="break-all font-mono text-[11px] text-slate-800 dark:text-slate-200"
        title={arn}
      >
        {truncateMiddle(arn)}
      </div>
      <Text variant="caption">Registered profile ARN</Text>
    </div>
  );
};

const BedrockStatusCell: FC<{
  status: ReturnType<typeof resolveBedrockRowStatus>;
}> = ({ status }) => (
  <div className="space-y-1">
    <Badge size="sm" variant={status.badge}>
      {status.label}
    </Badge>
    <Text as="div" className="max-w-xs leading-5">
      {status.detail}
    </Text>
  </div>
);

const BedrockArnSpendRow: FC<{
  entry: DashboardKeystoreEntry;
  spendingInfo: DashboardAdminKeystoreSpendingEntry | undefined;
  spendingLoading: boolean;
}> = ({ entry, spendingInfo, spendingLoading }) => {
  const spending = formatSpending(spendingInfo, spendingLoading);
  const status = resolveBedrockRowStatus(entry, spendingInfo);
  const arn = readBedrockInferenceProfileArn(entry);

  return (
    <DataTable.Row>
      <DataTable.Cell className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
        {entry.provider_key_id ?? "—"}
      </DataTable.Cell>
      <DataTable.Cell className="max-w-[26rem]">
        <BedrockArnCell arn={arn} />
      </DataTable.Cell>
      <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
        {entry.assigned_org_name ?? "—"}
      </DataTable.Cell>
      <DataTable.Cell>
        <BedrockStatusCell status={status} />
      </DataTable.Cell>
      <DataTable.Cell className={`text-xs ${spending.className}`}>
        {spending.text}
      </DataTable.Cell>
    </DataTable.Row>
  );
};

function resolveBedrockRowStatus(
  entry: DashboardKeystoreEntry,
  spendingInfo: DashboardAdminKeystoreSpendingEntry | undefined,
): {
  badge: "default" | "success" | "warning" | "status";
  detail: string;
  label: string;
} {
  if (spendingInfo?.error === "missing_provider_key") {
    return {
      badge: "warning",
      detail: "This entry needs a registered Bedrock model ID.",
      label: "model missing",
    };
  }
  if (
    spendingInfo?.error === "unassigned_entry" ||
    entry.status !== "assigned"
  ) {
    return {
      badge: "status",
      detail:
        "Assign this ARN to an organization before usage can be attributed.",
      label: "unassigned",
    };
  }
  if ((spendingInfo?.amount_usd ?? 0) > 0) {
    return {
      badge: "success",
      detail: "Usage was attributed for the selected period.",
      label: "attributed",
    };
  }
  return {
    badge: "default",
    detail: "No Bedrock usage matched this ARN in the selected period.",
    label: "no usage",
  };
}

const BedrockArnSpendPanel: FC<{
  entries: DashboardKeystoreEntry[];
  spendingMap: Map<string, DashboardAdminKeystoreSpendingEntry>;
  spendingLoading: boolean;
}> = ({ entries, spendingMap, spendingLoading }) => {
  const totalSpend = entries.reduce((sum, entry) => {
    const amount = spendingMap.get(entry.entry_id)?.amount_usd;
    return typeof amount === "number" ? sum + amount : sum;
  }, 0);
  const assignedCount = entries.filter(
    (entry) => entry.status === "assigned",
  ).length;
  const usageCount = entries.filter((entry) => {
    const amount = spendingMap.get(entry.entry_id)?.amount_usd;
    return typeof amount === "number" && amount > 0;
  }).length;

  return (
    <PanelSection
      badge={
        <Badge variant="info" size="sm">
          {entries.length} registered
        </Badge>
      }
      title="Bedrock ARN Cost Attribution"
    >
      <div className="space-y-4">
        <StatsGrid
          columns={2}
          items={[
            {
              label: "Registered ARNs",
              value: (
                <span className="font-mono text-lg font-semibold text-slate-900 dark:text-white">
                  {entries.length}
                </span>
              ),
            },
            {
              label: "Assigned ARNs",
              value: (
                <span className="font-mono text-lg font-semibold text-slate-900 dark:text-white">
                  {assignedCount}
                </span>
              ),
            },
            {
              label: "ARNs With Usage",
              value: (
                <span className="font-mono text-lg font-semibold text-slate-900 dark:text-white">
                  {usageCount}
                </span>
              ),
            },
            {
              label: "Period Cost",
              value: (
                <span className="font-mono text-lg font-semibold text-slate-900 dark:text-white">
                  {spendingLoading ? "..." : formatUsd(totalSpend)}
                </span>
              ),
            },
          ]}
        />

        <Text as="p" className="max-w-4xl leading-5">
          Costs are matched from persisted usage by current organization
          assignment plus the registered Bedrock model ID, then displayed
          against the stored inference profile ARN.
        </Text>

        <DataTable responsive>
          <DataTable.Header>
            <tr>
              <DataTable.HeaderCell>Model ID</DataTable.HeaderCell>
              <DataTable.HeaderCell>Registered ARN</DataTable.HeaderCell>
              <DataTable.HeaderCell>Assigned To</DataTable.HeaderCell>
              <DataTable.HeaderCell>Status</DataTable.HeaderCell>
              <DataTable.HeaderCell>Period Cost</DataTable.HeaderCell>
            </tr>
          </DataTable.Header>
          <DataTable.Body>
            {entries.map((entry) => (
              <BedrockArnSpendRow
                key={`bedrock-${entry.entry_id}`}
                entry={entry}
                spendingInfo={spendingMap.get(entry.entry_id)}
                spendingLoading={spendingLoading}
              />
            ))}
          </DataTable.Body>
        </DataTable>
      </div>
    </PanelSection>
  );
};

const KeystoreRow: FC<{
  entry: DashboardKeystoreEntry;
  organizations: { organization_id: string; name: string }[];
  spendingInfo: DashboardAdminKeystoreSpendingEntry | undefined;
  spendingLoading: boolean;
  onAssign: (entryId: string, orgId: string) => Promise<void>;
  onUnassign: (entryId: string) => Promise<void>;
  onDelete: (entryId: string) => Promise<void>;
}> = ({
  entry,
  organizations,
  spendingInfo,
  spendingLoading,
  onAssign,
  onUnassign,
  onDelete,
}) => {
  const [assigningOrgId, setAssigningOrgId] = useState<string | null>(null);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleAssign = useCallback(async () => {
    if (!assigningOrgId) {
      return;
    }
    setRowError(null);
    setActionLoading("assign");
    try {
      await onAssign(entry.entry_id, assigningOrgId);
      setShowOrgPicker(false);
      setAssigningOrgId(null);
    } catch (error) {
      setRowError(error instanceof Error ? error.message : "Failed to assign");
    } finally {
      setActionLoading(null);
    }
  }, [entry.entry_id, assigningOrgId, onAssign]);

  const handleUnassign = useCallback(async () => {
    setRowError(null);
    setActionLoading("unassign");
    try {
      await onUnassign(entry.entry_id);
    } catch (error) {
      setRowError(
        error instanceof Error ? error.message : "Failed to unassign",
      );
    } finally {
      setActionLoading(null);
    }
  }, [entry.entry_id, onUnassign]);

  const handleDelete = useCallback(async () => {
    setRowError(null);
    setActionLoading("delete");
    try {
      await onDelete(entry.entry_id);
    } catch (error) {
      setRowError(error instanceof Error ? error.message : "Failed to delete");
    } finally {
      setActionLoading(null);
    }
  }, [entry.entry_id, onDelete]);

  const spending = formatSpending(spendingInfo, spendingLoading);

  return (
    <>
      <DataTable.Row>
        <DataTable.Cell className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
          {PROVIDER_LABELS[entry.provider] ?? entry.provider}
        </DataTable.Cell>
        <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
          {entry.label ?? "—"}
        </DataTable.Cell>
        <DataTable.Cell>
          <Badge
            variant={entry.status === "available" ? "success" : "default"}
            size="sm"
          >
            {entry.status}
          </Badge>
        </DataTable.Cell>
        <DataTable.Cell className="text-xs text-gray-700 dark:text-gray-300">
          {entry.assigned_org_name ?? "—"}
        </DataTable.Cell>
        <DataTable.Cell className={`text-xs ${spending.className}`}>
          {spending.text}
        </DataTable.Cell>
        <DataTable.Cell className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
          {formatRelativeTime(new Date(entry.created_at).getTime())}
        </DataTable.Cell>
        <DataTable.Cell>
          <div className="flex items-center gap-2">
            {entry.status === "available" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowOrgPicker(!showOrgPicker);
                    setRowError(null);
                  }}
                >
                  Assign
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => void handleDelete()}
                  loading={actionLoading === "delete"}
                  loadingText="Deleting..."
                >
                  Delete
                </Button>
              </>
            )}
            {entry.status === "assigned" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleUnassign()}
                loading={actionLoading === "unassign"}
                loadingText="Unassigning..."
              >
                Unassign
              </Button>
            )}
          </div>
        </DataTable.Cell>
      </DataTable.Row>

      {/* Org picker row */}
      {showOrgPicker && entry.status === "available" && (
        <tr>
          <td
            colSpan={7}
            className="border-t border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-700 dark:bg-white/5"
          >
            {rowError && (
              <Alert variant="error" size="sm" className="mb-2">
                {rowError}
              </Alert>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="w-full sm:w-64">
                <FormSelect
                  label="Organization"
                  size="sm"
                  options={[
                    { label: "Select organization…", value: "" },
                    ...organizations.map((o) => ({
                      label: o.name,
                      value: o.organization_id,
                    })),
                  ]}
                  value={assigningOrgId ?? ""}
                  onChange={(e) => setAssigningOrgId(e.target.value || null)}
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleAssign()}
                loading={actionLoading === "assign"}
                loadingText="Assigning..."
                disabled={!assigningOrgId}
              >
                Confirm Assign
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowOrgPicker(false);
                  setAssigningOrgId(null);
                  setRowError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
